/**
 * Links the browser's Mixpanel identity to the signed-in TED user.
 *
 * The Rails side writes user profile properties (e.g. `streakLength`) keyed on
 * `ted_user_id` — see app/app/services/mixpanel_tracking/. Until the browser
 * calls `identify()` with that same ID, its events stay under an anonymous
 * `$device:` ID, Mixpanel treats the two as separate users, and the profile
 * properties can't be joined to events in Insights reports.
 */

/**
 * The slice of the Mixpanel browser SDK this module touches.
 *
 * The instance is passed in rather than imported: each game installs its own
 * copy of `mixpanel-browser`, and importing one here would let the bundler pull
 * in a second SDK whose in-memory identity is not the one the game's own
 * `mixpanel.track()` calls read from.
 */
export interface MixpanelIdentityClient {
  get_property(property_name: string): unknown;
  identify(unique_id?: string): unknown;
  reset(): void;
}

const CURRENT_USER_QUERY = /* GraphQL */ `
  query GetCurrentUserTedId {
    currentUser {
      tedUserId
    }
  }
`;

interface CurrentUserResponse {
  currentUser: { tedUserId: number | null } | null;
}

type TedIdentity =
  /** The games API told us who is signed in. */
  | { status: "signed-in"; tedUserId: string }
  /** The games API answered, and nobody is signed in. */
  | { status: "signed-out" }
  /** We couldn't ask, so we know nothing either way. */
  | { status: "unknown" };

async function fetchTedIdentity(): Promise<TedIdentity> {
  const endpoint = import.meta.env.VITE_GAME_SERVER_API;

  if (!endpoint) {
    return { status: "unknown" };
  }

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // The session cookie belongs to the games API's own domain, so it only
      // rides along on credentialed requests.
      credentials: "include",
      body: JSON.stringify({ query: CURRENT_USER_QUERY }),
    });

    if (!response.ok) {
      return { status: "unknown" };
    }

    const { data, errors } = (await response.json()) as {
      data?: CurrentUserResponse | null;
      errors?: unknown[];
    };

    // A missing `data` key or a GraphQL error means the question went
    // unanswered — which is not the same as "signed out".
    if (!data || (errors && errors.length > 0)) {
      return { status: "unknown" };
    }

    const tedUserId = data.currentUser?.tedUserId;

    return tedUserId == null
      ? { status: "signed-out" }
      : { status: "signed-in", tedUserId: String(tedUserId) };
  } catch {
    return { status: "unknown" };
  }
}

let inFlight: Promise<void> | null = null;

async function identify(mixpanel: MixpanelIdentityClient): Promise<void> {
  const identity = await fetchTedIdentity();

  if (identity.status !== "signed-in") {
    // Deliberately no `reset()` here. The Mixpanel cookie is shared across
    // ted.com subdomains, and a visitor can be signed in to ted.com without yet
    // holding a games API session — clearing the identity would throw away one
    // the rest of ted.com set correctly.
    return;
  }

  const currentProperty = mixpanel.get_property("$user_id");
  const identifiedAs =
    typeof currentProperty === "string" ? currentProperty : null;

  if (identifiedAs === identity.tedUserId) {
    return;
  }

  if (identifiedAs) {
    // A different TED account is signed in on this browser. Reset first so the
    // new user starts from a fresh device ID rather than being merged into the
    // previous user's identity cluster.
    mixpanel.reset();
  }

  mixpanel.identify(identity.tedUserId);
}

/**
 * Identify the signed-in TED user with Mixpanel, if there is one.
 *
 * Safe to call on every app boot: it is a no-op when the visitor is signed out,
 * when the games API can't be reached, or when this browser is already
 * identified as the same user. Call it as early as possible — right after
 * `mixpanel.init()` — so the whole session gets linked. Events that fire before
 * it resolves are still attributed correctly, because `identify()` sends an
 * `$identify` event carrying the previous anonymous ID.
 */
export function identifyTedUser(
  mixpanel: MixpanelIdentityClient,
): Promise<void> {
  inFlight = inFlight ?? identify(mixpanel);

  return inFlight;
}
